const CAP_ECDSA = {
  id: 215,
  title: "El Algoritmo ECDSA",
  slug: "algoritmo-ecdsa",
  type: "lesson",
  category: "bitcoin-avanzado",
  level: "advanced",
  preview:
    "ECDSA es el algoritmo de firma digital utilizado por Bitcoin. Permite generar claves privadas, claves públicas y firmas digitales seguras mediante criptografía de curva elíptica.",
  sections: [
    {
      heading: "¿Qué es ECDSA?",
      text: [
        "El algoritmo ECDSA funciona mediante un sistema de criptografía asimétrica basado en curvas elípticas. Su estructura permite generar dos claves matemáticamente relacionadas: una clave privada y una clave pública.",
        "El funcionamiento se basa en operaciones sobre una curva elíptica que hacen prácticamente imposible obtener la clave privada desde la clave pública.",
        "Gracias a su nivel de seguridad, ECDSA se usa ampliamente en certificados de Internet (SSL/TLS), sistemas de autenticación y, por supuesto, en Bitcoin."
      ],
      bluepoints: [
        "Firma única para cada par de claves.",
        "Imposibilidad práctica de falsificar firmas.",
        "Altísimo nivel de seguridad con bajo costo computacional.",
        "Basado en curvas elípticas seguras.",
        "Estándar usado globalmente en sistemas criptográficos modernos."
      ]
    },

    {
      heading: "Clave Privada, Clave Pública y Firma",
      text: [
        "ECDSA se basa en tres componentes esenciales: la clave privada, la clave pública y la firma digital.",
        "Cada una cumple un rol único dentro del proceso de firmar y verificar información."
      ],
      table: {
        columns: ["Clave Privada", "Clave Pública", "Firma Digital"],
        rows: [
          {
            "Clave Privada":
              "Número secreto generado aleatoriamente. Es el único dato que permite gastar los fondos asociados en el sistema Bitcoin.",
            "Clave Pública":
              "Derivada matemáticamente de la clave privada. Se comparte abiertamente para verificar firmas, pero no permite obtener la clave privada.",
            "Firma Digital":
              "Resultado matemático obtenido del hash del mensaje y la clave privada. Produce dos valores: 'r' y 's'. Permite verificar autenticidad sin revelar la clave privada."
          },
          {
            "Clave Privada": "Debe mantenerse en secreto absoluto.",
            "Clave Pública":
              "Sirve como identificador público y para validar la firma.",
            "Firma Digital":
              "Permite demostrar que el firmante poseía la clave privada correspondiente."
          }
        ]
      }
    },

    {
      heading: "¿Cómo verifica una firma ECDSA?",
      text: [
        "Cuando alguien recibe un mensaje firmado, utiliza la clave pública del autor para realizar una verificación matemática.",
        "Si la firma es válida, el sistema confirma que:",
        "1. El mensaje fue firmado con la clave privada correspondiente.",
        "2. El contenido no fue modificado.",
        "3. No se requiere revelar la clave privada en ningún momento."
      ],
      notes: [
        "ECDSA permite autenticidad sin revelar información sensible.",
        "Es un sistema matemáticamente seguro gracias a la dificultad del logaritmo discreto."
      ]
    },

    {
      heading: "ECDSA y Bitcoin",
      text: [
        "Uno de los retos de Satoshi Nakamoto al diseñar Bitcoin fue elegir un sistema seguro, rápido y eficiente para generar claves y validar firmas.",
        "ECDSA, especialmente usando la curva secp256k1, cumplía con todas las características necesarias.",
        "Este estándar permitió crear direcciones más cortas combinando codificación Base58Check y funciones hash como SHA-256 y RIPEMD-160."
      ],
      bluepoints: [
        "Bitcoin usa la curva elíptica secp256k1, altamente auditada y segura.",
        "Las claves públicas se derivan de la privada mediante una relación matemática unidireccional.",
        "El proceso es eficiente incluso en hardware modesto.",
        "Permite generar infinitas claves públicas posibles.",
        "El tamaño de las claves se reduce usando Base58, SHA-256 y RIPEMD-160."
      ]
    }
  ]
};

export default CAP_ECDSA;
